import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Grid, Loader, Segment } from "semantic-ui-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
	dataLayerQueryOptions,
	dataLayerFieldAssociationsQueryOptions,
	deleteDataLayerFieldAssociation,
} from "../../../../apis/dataLayerApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import VectorDataContent from "./VectorDataContent";
import RasterDataContent from "./RasterDataContent";
import styles from "./styles.module.css";

interface VectorDataLayerUploadProps {
	// callback for controlling FlightProcess ribbon navigation. Setting to true enables the Field Association step.
	// (completion applies to vector data uploads, not source raster uploads)
	uploadsComplete: boolean;
	onUploadsComplete: (nextComplete: boolean) => void;
}

/**
 * Uploader component which manages a list of Vector Data Uploads for user's selected DataLayer.
 */
const VectorDataLayerUpload = ({ uploadsComplete, onUploadsComplete }: VectorDataLayerUploadProps) => {
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };
	const userAuth = useUserAuth() as { currentClientId: string };
	const moduleNavigation = useModuleNavigation() as {
		flightId: string;
		createFlightLink: (useIdsFromUrl: boolean, flightId: string | null, submodule: string | null) => string;
	};
	const history = useHistory();

	const [vectorLoading, setVectorLoading] = useState(true);
	const [rasterLoading, setRasterLoading] = useState(true);
	const anyChildLoading = vectorLoading || rasterLoading;

	const dataLayerId = moduleNavigation.flightId; // workaround for hijacking the Flight Wizard

	const { data: dataLayerOptions, error: dataLayerOptionsError } = useQuery(
		dataLayerQueryOptions(getTokenSilently, userAuth.currentClientId),
	);

	const {
		data: fieldAssociations,
		error: fieldAssociationsError,
		refetch: fieldAssociationsRefetch,
	} = useQuery(dataLayerFieldAssociationsQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId));

	const dataLayerHasExistingFieldAssociations = !!fieldAssociations && fieldAssociations.length > 0;

	const { mutate: resetExistingFieldAssociations } = useMutation({
		mutationFn: async () => {
			if (!fieldAssociations || fieldAssociations.length === 0) return;
			const clientId = userAuth.currentClientId;
			for (const fieldId of fieldAssociations) {
				await deleteDataLayerFieldAssociation({
					getAccessToken: getTokenSilently,
					clientId,
					dataLayerId,
					fieldId,
				});
			}
		},
		onSuccess: () => {
			void fieldAssociationsRefetch();
		},
		onError: (error) => {
			console.error(error);
			toast.error(error instanceof Error ? error.message : String(error));
		},
	});

	useEffect(() => {
		if (dataLayerOptionsError !== null) {
			console.error(dataLayerOptionsError);
			toast.error(dataLayerOptionsError.message);
		}
	}, [dataLayerOptionsError]);

	useEffect(() => {
		if (fieldAssociationsError !== null) {
			console.error(fieldAssociationsError);
			toast.error(fieldAssociationsError.message);
		}
	}, [fieldAssociationsError]);

	const handleBackButton = useCallback(() => {
		history.push(moduleNavigation.createFlightLink(true, null, "info"));
	}, [history, moduleNavigation]);

	const handleContinue = useCallback(() => {
		history.push(moduleNavigation.createFlightLink(true, dataLayerId, "data-layer-assoc-fields"));
	}, [history, moduleNavigation, dataLayerId]);

	return (
		<>
			<Segment basic vertical className={`${styles.segment} ${styles.uploadActionsSegment}`}>
				<Grid stackable className={styles.uploadActionsRow}>
					<Grid.Row verticalAlign="middle">
						<Grid.Column computer={6} className={styles.uploadActionsBackCol}>
							<Button secondary compact content="Back" onClick={handleBackButton} />
						</Grid.Column>
						<Grid.Column computer={10} textAlign="right" className={styles.uploadActionsButtonsCol}>
							<div className={styles.uploadActionsButtonGroup}>
								<Button primary compact content="Continue" disabled={!uploadsComplete} onClick={handleContinue} />
							</div>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</Segment>

			{anyChildLoading && (
				<div className={styles.loader}>
					<Loader active inline="centered" />
				</div>
			)}

			<VectorDataContent
				dataLayerId={dataLayerId}
				dataLayerOptions={dataLayerOptions}
				dataLayerHasExistingFieldAssociations={dataLayerHasExistingFieldAssociations}
				onResetExistingFieldAssociations={() => resetExistingFieldAssociations()}
				onUploadsComplete={onUploadsComplete}
				onLoadingChange={setVectorLoading}
			/>

			<RasterDataContent
				dataLayerId={dataLayerId}
				dataLayerOptions={dataLayerOptions}
				dataLayerHasExistingFieldAssociations={dataLayerHasExistingFieldAssociations}
				onResetExistingFieldAssociations={() => resetExistingFieldAssociations()}
				onLoadingChange={setRasterLoading}
			/>
		</>
	);
};

export default VectorDataLayerUpload;
